/**
 * Compare-segments shared toggle primitives (Step 3 foundation, hoisted for Wave 3).
 *
 * The Mode-B ("toggle-per-cut") building blocks the segmented exhibits share: the
 * "Everyone"-anchored band ordering, the pill cut-toggle, the reduced-motion-safe swap
 * transition, the active-band bookkeeping, and the thin-cell caveat line. They lived in
 * PlotRenderer.tsx while only the response-derived band charts used them; the Wave-3
 * scorecard (in DivergingStackedRating.tsx) needs the SAME toggle, so they move to this
 * leaf module — reused by both, no import cycle (PlotRenderer already imports the
 * scorecard from DivergingStackedRating). Presentational only; no chart shape assumed.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { CUT_SUPPRESS_N } from './divergingStacked'
import { breakdownRows, thinCellCaveat } from './segmentBreakdown'
import { useQuickStudyNoun } from './quickStudyNoun'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'
import type { CutIdSlice } from './quickStudyPure'

/** One rendered band: the pooled "Everyone" anchor or one selected cut, plus its slice. The
 *  "Everyone" anchor is never suppressed/directional (the seam only flags CUT slices). */
export interface SegBand<T = unknown> {
  cutId: string
  label: string
  n: number
  responses: T[]
  suppressed: boolean
  directional: boolean
}

/** The rendered band order: the pooled "Everyone" anchor first, then the selected cuts in
 *  ordinal band order (breakdownRows — age bands ascending, etc., never selection/size).
 *  Carries the seam's `suppressed`/`directional` verdict through unchanged (R1.1) — callers
 *  decide whether to drop a suppressed band or mark a directional one; this helper never
 *  filters, so a caller that ignores the flags keeps today's byte-identical rendering. */
export function orderedSegBands<T>(
  anchorResponses: T[],
  cutSlices: Array<CutIdSlice<T>>,
): Array<SegBand<T>> {
  const anchor: SegBand<T> = {
    cutId: '__all__',
    label: 'Everyone',
    n: anchorResponses.length,
    responses: anchorResponses,
    suppressed: false,
    directional: false,
  }
  const cuts = breakdownRows(cutSlices.map((s) => ({ label: s.label, value: s.n, n: s.n, slice: s }))).map((b) => ({
    cutId: b.slice.cutId,
    label: b.slice.label,
    n: b.slice.n,
    responses: b.slice.responses,
    suppressed: b.slice.suppressed,
    directional: b.slice.directional,
  }))
  return [anchor, ...cuts]
}

/** Subtle, reduced-motion-safe cross-fade on plot swap (Mode B toggle / cut apply). Keyed
 *  remount + tailwindcss-animate fade; the hoisted `usePrefersReducedMotion` gates it so a
 *  reduced-motion user gets an instant swap. */
export function SegmentSwap({ swapKey, children }: { swapKey: string; children: ReactNode }) {
  const reduced = usePrefersReducedMotion()
  return (
    <div key={swapKey} className={cn(!reduced && 'animate-in fade-in duration-200')} data-testid="qs-segment-swap">
      {children}
    </div>
  )
}

/** A lightweight band descriptor for the toggle chrome (only cutId/label/n are read). */
export interface ToggleBand {
  cutId: string
  label: string
  n: number
}

/** Mode B control: a pill toggle to show ONE cut (or Everyone) at a time. */
export function CutToggle({ bands, active, onSelect }: { bands: Array<ToggleBand>; active: string; onSelect: (cutId: string) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-[11px]" data-testid="qs-segment-toggle">
      {bands.map((b) => {
        const on = b.cutId === active
        return (
          <button
            key={b.cutId}
            type="button"
            onClick={() => onSelect(b.cutId)}
            data-testid={`qs-segment-toggle-${b.cutId}`}
            aria-pressed={on}
            className={cn(
              'rounded-full px-2 py-0.5 transition-colors',
              on
                ? 'bg-twin-chat-send text-twin-chat-send-fg'
                : 'border border-twin-chat-divider text-twin-chat-fg-muted hover:text-twin-chat-fg',
            )}
          >
            {b.label} <span className="tabular-nums opacity-70">n={b.n}</span>
          </button>
        )
      })}
    </div>
  )
}

/** Keep a Mode B active-cut selection valid as the picker's selection changes. */
export function useActiveBand(bands: Array<{ cutId: string }>): [string, (id: string) => void] {
  const [active, setActive] = useState<string>(() => bands[0]?.cutId ?? '__all__')
  useEffect(() => {
    if (!bands.some((b) => b.cutId === active)) setActive(bands[0]?.cutId ?? '__all__')
  }, [bands, active])
  return [active, setActive]
}

/** Wave-7 caveat dedup: when the CARD already prints its own named small-sample note
 *  (MessageRow's `qs-small-n`, which fires at MIN_SEGMENT_N=10 ≥ THIN_CELL_MIN=8, so it
 *  always covers a thin cell), the exhibit-level blanket line would stack a second caveat
 *  under the same plot. The card provides `true` here to compress to the one named line;
 *  outside that provider (tests, any future non-card consumer) the caveat renders. */
export const BandCaveatSuppressedContext = createContext(false)

/** The shared thin-cell caveat line (a cut band under THIN_CELL_MIN twins can swing on a
 *  few twins). Renders nothing when the card-level note already covers it (see
 *  {@link BandCaveatSuppressedContext}). */
export function BandCaveat() {
  const suppressed = useContext(BandCaveatSuppressedContext)
  const { noun } = useQuickStudyNoun()
  if (suppressed) return null
  return (
    <p className="text-[11px] text-amber-500" data-testid="qs-breakdown-caveat">
      ⚠ {thinCellCaveat(noun)}
    </p>
  )
}

/** R1.1 — names the cuts the seam dropped from the render (n < CUT_SUPPRESS_N), so a marketer
 *  sees WHY a selected cut is missing instead of it silently vanishing. Mirrors
 *  DivergingStackedRating's own `qs-diverging-suppressed-note` line so every Compare-segments
 *  consumer states the same floor the same way. Renders nothing when nothing was suppressed. */
export function SuppressedCutsNote({ labels }: { labels: Array<string> }) {
  const { t } = useTranslation('app')
  const { noun } = useQuickStudyNoun()
  if (labels.length === 0) return null
  const key = noun === 'respondents' ? 'quickStudyChrome.cutsSuppressedRespondents' : 'studyReport.cutsSuppressed'
  return (
    <p className="text-[11px] text-amber-500" data-testid="qs-segment-suppressed-note">
      ⚠ {t(key, { labels: labels.join(', '), floor: CUT_SUPPRESS_N })}
    </p>
  )
}
